import { paddingUtil, paddingVertical } from "@gadgetinc/themes/src/utils";
import { useStyletron } from "baseui";
// eslint-disable-next-line no-restricted-imports
import type { ButtonProps as BaseUIButtonProps } from "baseui/button";
// eslint-disable-next-line no-restricted-imports
import { KIND, Button as RawButton, SIZE } from "baseui/button";
import { merge, omit } from "lodash";
import React, { forwardRef } from "react";
import type { StyleObject } from "styletron-react";
import { KeyNormalizedControl } from "./KeyNormalizedControl";
// eslint-disable-next-line no-restricted-imports
export { KIND, SHAPE, SIZE, StyledLoadingSpinner } from "baseui/button";

export type ButtonProps = Omit<BaseUIButtonProps, "kind" | "size"> & {
  kind?: KIND[keyof KIND] | "ghost";
  size?: SIZE[keyof SIZE] | "tight" | "square";
  tabIndex?: number;
  className?: string;
  "data-testid"?: string;
  normalizeKeys?: boolean;
  autoFocus?: boolean;
};
/**
 * BaseUI reexport for minimal buttons because we need to pass overrides to style the text right. Wherever you want to use a <Button />, you should use this instead.
 * See https://github.com/uber/baseweb/issues/106 for more information
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const [_css, $theme] = useStyletron();
  const rawProps: BaseUIButtonProps = {};

  let rawKind: KIND[keyof KIND] | undefined;
  if (props.kind == "ghost") {
    rawKind = KIND.minimal;
  } else {
    rawKind = props.kind;
  }

  const rawSize: SIZE[keyof SIZE] | undefined = props.size == "tight" ? SIZE.compact : props.size == "square" ? SIZE.default : props.size;

  const overrides = {
    BaseButton: {
      style: {
        cursor: props.disabled ? "not-allowed !important" : "pointer",
        transitionProperty: "background, background-color",
        transitionDuration: "0s",
        ":focus-visible": {
          boxShadow: "none",
          outline: `2px solid ${$theme.colors.borderFocus}`,
          outlineOffset: "1px",
        },
        userSelect: "none",
      } as StyleObject,
    },
  };

  if (props.kind == "ghost") {
    const focusStyle = { color: $theme.colors.contentPrimary, backgroundColor: "transparent" };
    merge(overrides, {
      BaseButton: {
        style: {
          ...paddingUtil($theme.sizing.scale100, $theme.sizing.scale300),
          color: $theme.colors.contentTertiary,
          ":focus-visible": focusStyle,
          ":hover": focusStyle,
        },
      },
    });
  } else if (rawKind === KIND.minimal || rawKind === KIND.tertiary) {
    merge(overrides, {
      BaseButton: {
        style: {
          ...paddingUtil($theme.sizing.scale100, $theme.sizing.scale300),
          textDecoration: "underline",
        },
      },
    });
  }

  switch (props.size) {
    case "square": {
      merge(overrides, {
        BaseButton: {
          style: paddingUtil($theme.sizing.scale100),
        },
      });
      break;
    }
    case "tight": {
      merge(overrides, {
        BaseButton: {
          style: paddingUtil($theme.sizing.scale0),
        },
      });
      break;
    }
    case SIZE.large: {
      merge(overrides, {
        BaseButton: {
          style: paddingVertical($theme.sizing.scale500),
        },
      });
      break;
    }
    case undefined:
    case SIZE.default: {
      merge(overrides, {
        BaseButton: {
          style: paddingUtil($theme.sizing.scale300, $theme.sizing.scale500),
        },
        StartEnhancer: {
          style: {
            marginRight: $theme.sizing.scale400,
          },
        },
      });
    }
  }

  merge(overrides, props.overrides);
  // normalizeKeys is not a valid prop for Button
  merge(rawProps, omit(props, ["normalizeKeys", "children"]));
  if (props.$style) {
    merge(overrides.BaseButton.style, props.$style);
  }

  const button = (
    // Passing raw props with children via spread causes react to complain about "Each child in a list should have a unique "key" prop". Let's explictly pass children to the RawButton component
    <RawButton ref={ref} {...rawProps} overrides={overrides} kind={rawKind} size={rawSize}>
      {props.children}
    </RawButton>
  );

  return props.normalizeKeys ? (
    <KeyNormalizedControl mode="simple" ignoreKeyProps={{ style: { lineHeight: "0" } }}>
      {button}
    </KeyNormalizedControl>
  ) : (
    button
  );
});
